import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import "../../../style/MillingResourceOverview.css";
import { apiAxios } from "../../../utils/apiAxios";
import { Hub } from "./Interfaces/Hub";
import { Machine } from "./Interfaces/Machine";
import { KpiSummary } from "./Interfaces/KpiSummary";


/* ============================
   TABLE COLUMNS
============================ */

const hubColumns: TableColumn<Hub>[] = [
  { name: "Hub Name", selector: row => row.hubName, sortable: true },
  { name: "Total Machines", selector: row => row.totalMachines, sortable: true },
  { name: "Running", selector: row => row.running, sortable: true },
  { name: "Idle", selector: row => row.idle, sortable: true },
  { name: "Down", selector: row => row.down, sortable: true },
];

const machineColumns: TableColumn<Machine>[] = [
  { name: "Machine ID", selector: row => row.machineID, sortable: true },
  { name: "Name", selector: row => row.companyName, sortable: true },
  {
    name: "Status",
    cell: row => {
      let color = "gray";
      if (row.actualStatus === "Running") color = "green";
      else if (row.actualStatus === "Idle") color = "orange";
      else if (row.actualStatus === "Down") color = "red";
      // else if (row.jobStatus === "Milling Completed") color = "blue";
      
      return <span style={{ color, fontWeight: "bold" }}>{row.actualStatus}</span>;
    }
  },
  {
    name: "Assigned Job",
    cell: row => (
      <span >
        {row.assignedJob}
      </span>
    )
  },
  // { name: "Started By", selector: row => row.startedByName || "-" },
  // { name: "Est. Time", selector: row => row.estimatedMinutes || 0 },
  // { name: "Actual Time", selector: row => row.actualMinutes || 0 },
];

/* ============================
   COMPONENT
============================ */

const ManagerMillingResourceOverview: React.FC = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [hubsData, setHubsData] = useState<Hub[]>([]);
  const [machinesData, setMachinesData] = useState<Machine[]>([]);
  const [selectedHub, setSelectedHub] = useState<Hub | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // New state for KPI data from API
  const [kpiData, setKpiData] = useState<KpiSummary>({
    onlineMachines: 0,
    offlineMachines: 0,
    totalHubs: 0,
    totalMachines: 0
  });

  /* ============================
     FETCH DATA
  ============================ */

  const fetchOverview = async () => {
    try {
      setLoading(true);

      const res = await apiAxios.get(
        `${API_BASE}/Central/milling-resource-overview`);

      const kpiSummary = res.data.kpiSummary;
      const hubs = res.data.hubs;
      const machines = res.data.machines;

      if (kpiSummary) {
        setKpiData({
          onlineMachines: kpiSummary.onlineMachines || 0,
          offlineMachines: kpiSummary.offlineMachines || 0,
          totalHubs: kpiSummary.totalHubs || 0,
          totalMachines: kpiSummary.totalMachines || 0,
          runningJobs: kpiSummary.runningJobs || 0,
          idleMachines: kpiSummary.idleMachines || 0,
          downMachines: kpiSummary.downMachines || 0
        });
      }

      //--------------------------------------------------
      // 🔹 HUBS
      //--------------------------------------------------
      const formattedHubs: Hub[] = hubs.map((h: any) => ({
        hubName: h.hubName || `Hub ${h.millingHubID || ''}`,
        millingHubID: h.millingHubID || 0,
        totalMachines: h.totalMachines || 0,
        running: h.running || 0,
        idle: h.idle || 0,
        down: h.down || 0,
        onlineMachines: h.onlineMachines || 0,
        offlineMachines: h.offlineMachines || 0,
        machines: []
      }));

      //--------------------------------------------------
      // 🔹 MACHINES
      //--------------------------------------------------
      const formattedMachines: Machine[] = machines.map((m: any) => ({
        machineID: m.machineID,
        millingHubID: m.millingHubID,
        hubName: m.hubName || `Hub ${m.millingHubID}`,
        jobStatus: m.jobStatus || "UNKNOWN",
        actualStatus:m.actualStatus || "UNKNOWN",
        
        estimatedMinutes: m.estimatedMinutes || 0,
        actualMinutes: m.actualMinutes || 0,
        startedByName: m.startedByName,
        companyName: m.companyName,
        assignedJob: m.orderID || "Offline",
        
      }));

      setHubsData(formattedHubs);
      setMachinesData(formattedMachines);

    } catch (err) {
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  /* ============================
     ROW CLICK
  ============================ */

  const handleRowClick = (row: Hub) => {
    const filteredMachines = machinesData.filter(m => 
      m.hubName === row.hubName || 
      m.hubName.includes(row.hubName) ||
      m.millingHubID === row.millingHubID
    );
    
    setSelectedHub({
      ...row,
      machines: filteredMachines
    });
    
    setShowModal(true);
  };

  /* ============================
     UI
  ============================ */

  return (
    <div className="container">
      <h4 className="mb-4">Milling Resource Overview</h4>

      {/* KPI CARDS - Using API data directly */}
    <div className="row order-status-bar mb-4">
<div className="col-md-12 col-lg-3 col-12">
    {/* Online */}
  <div className="stat-box2">
    <h2 className="stat-number">{kpiData.onlineMachines}</h2>
    <div className="stat-label green">
      <i className="bi bi-check-circle-fill"></i> Online Machines
    </div>
  </div>
</div>
<div className="col-md-12 col-lg-3 col-12">
  
  {/* Offline */}
  <div className="stat-box2">
    <h2 className="stat-number">{kpiData.offlineMachines}</h2>
    <div className="stat-label red">
      <i className="bi bi-x-circle-fill"></i> Offline Machines
    </div>
  </div>
</div>
<div className="col-md-12 col-lg-3 col-12">
    {/* Total Hubs */}
  <div className="stat-box2">
    <h2 className="stat-number">{kpiData.totalHubs}</h2>
    <div className="stat-label blue">
      <i className="bi bi-building"></i> Total Hubs
    </div>
  </div>
</div>
<div className="col-md-12 col-lg-3 col-12">
  
  {/* Total Machines */}
  <div className="stat-box2">
    <h2 className="stat-number">{kpiData.totalMachines}</h2>
    <div className="stat-label yellow">
      <i className="bi bi-hdd-network"></i> Total Machines
    </div>
  </div>
</div>







</div>

      {/* Optional: Additional KPI row if you want to show more stats */}
    {(kpiData.runningJobs !== undefined || kpiData.idleMachines !== undefined) && (
  <div className="row order-status-bar mb-4">
<div className="col-md-12 col-lg-3 col-12">
   {kpiData.runningJobs !== undefined && (
      <div className="stat-box2">
        <h2 className="stat-number">{kpiData.runningJobs}</h2>
        <div className="stat-label info">
          <i className="bi bi-play-circle-fill"></i> Running Jobs
        </div>
      </div>
    )}
  </div>
<div className="col-md-12 col-lg-3 col-12">
      {kpiData.idleMachines !== undefined && (
      <div className="stat-box2">
        <h2 className="stat-number">{kpiData.idleMachines}</h2>
        <div className="stat-label warning">
          <i className="bi bi-pause-circle-fill"></i> Idle Machines
        </div>
      </div>
    )}
  </div>
<div className="col-md-12 col-lg-3 col-12">
      {kpiData.downMachines !== undefined && (
      <div className="stat-box2">
        <h2 className="stat-number">{kpiData.downMachines}</h2>
        <div className="stat-label danger">
          <i className="bi bi-exclamation-circle-fill"></i> Down Machines
        </div>
      </div>
    )}
  </div>
<div className="col-md-12 col-lg-3 col-12">
  </div>
   
      
  
      
  

  </div>
)}
      {/* TABLE */}
      <div className="premium-card  rounded p-4">
        <DataTable
          columns={hubColumns}
          data={hubsData}
          progressPending={loading}
          highlightOnHover
          pointerOnHover
          pagination
          onRowClicked={handleRowClick}
         className="admin-delivery-table"
        />
      </div>

      {/* MODAL */}
      {showModal && selectedHub && (
        <div className="modal-overlay">
          <div className="modal-content milling-resource-over">
             <div className="d-flex justify-content-between">
                <h5>{selectedHub.hubName} - Machines</h5>
               
                             <button
  className="custom-close-btn btn-close "
  onClick={() => setShowModal(false)}
>
  <i className="bi bi-x customcross"></i>
</button>
          
            </div>
            <hr></hr>
            <div className="modaldatatablescroll">
            <DataTable
              columns={machineColumns}
              data={selectedHub.machines}
              pagination
              highlightOnHover
              customStyles={{
                headCells: {
                  style: {
                    fontWeight: "500",
                    fontSize: "14px",
                  },
                },
              }}
            />
          </div></div>
        </div>
      )}
    </div>
  );
};

export default ManagerMillingResourceOverview;
